import * as echarts from 'echarts';
import { EChartsInitOptions, createBaseOptionEChart } from './baseOption';
import { generateSeriesColors } from './defaultMetricsColors';
import { generateYAxisPositions } from './defaultYAxisPositions';
import { type Formatters } from '@/hooks/useFormatters';
import { MetricUnit } from 'src/__generated__/graphql';
import { formatXAxisLabel } from './xAxisFormatters';

export type SerieType = (
  | echarts.LineSeriesOption
  | echarts.BarSeriesOption
  | echarts.ScatterSeriesOption
  | echarts.CustomSeriesOption
) & {
  yAxisId: string;
  name: string;
  type: 'line' | 'bar' | 'scatter' | 'area' | 'custom' | 'heatmap';
};
export enum EChartsSymbolType {
  Circle = 'circle',
  Rect = 'rect',
  RoundRect = 'roundRect',
  Triangle = 'triangle',
  Diamond = 'diamond',
  Pin = 'pin',
  Arrow = 'arrow',
  None = 'none',
}

export type InternalSerie = {
  meta: {
    id?: string;
    name: string;
    type: 'line' | 'bar' | 'scatter' | 'area' | 'custom' | 'heatmap';
    data?: SerieType['data'];
    yAxisId: string;
    xAxisId?: string;
    dataType?: string;
    color?: string | null;
    skipFilter?: boolean;
  };
  value: SerieType;
};

type InternalYAxis = {
  meta: {
    id: string;
    type: echarts.YAXisComponentOption['type'];
    gridIndex?: number;
    unit?: string | MetricUnit | null;
    precision?: number | null;
  };
  value: echarts.YAXisComponentOption;
};

type InternalXAxis = {
  meta: {
    id: string;
    type: echarts.XAXisComponentOption['type'];
    gridIndex?: number;
  };
  value: echarts.XAXisComponentOption;
};

export default class EChartsOptionBuilder {
  private initOptions: EChartsInitOptions;
  private chartInstance: echarts.ECharts | null = null;
  private series: InternalSerie[] = [];
  private yAxis: InternalYAxis[] = [];
  private xAxis: InternalXAxis[] = [];
  private formatters: Formatters | null = null;
  private relevantYAxis: InternalYAxis[] = [];
  private relevantSeries: InternalSerie[] = [];
  private option: echarts.EChartsOption = {};

  constructor(initOptions: EChartsInitOptions = {}) {
    this.initOptions = initOptions;
  }

  setChartInstance(chartInstance: echarts.ECharts) {
    this.chartInstance = chartInstance;
    this.handleLegendsSelection();
  }

  setFormatters(formatters: Formatters) {
    this.formatters = formatters;
  }

  setInitOption(option: (base: EChartsInitOptions) => EChartsInitOptions) {
    this.initOptions = option(this.initOptions);
    return this;
  }

  private handleLegendsSelection() {
    if (!this.chartInstance) return;
    this.chartInstance.on('legendselectchanged', (event) => {
      const yAxisPositions = generateYAxisPositions(
        this.relevantYAxis.map((yAxis) => ({
          id: yAxis.meta.id,
          position: yAxis.value.position,
          gridIndex: yAxis.meta.gridIndex ?? 0,
          show: yAxis.value.show !== false,
        })),
        this.relevantSeries
          .filter((serie) => {
            const id = serie.meta.name;
            const selectedLegends = (event as { selected: { [name: string]: boolean } }).selected;
            if (id && selectedLegends && id in selectedLegends) {
              return selectedLegends[id];
            }
            return true;
          })
          .map((serie) => serie.meta.yAxisId),
      );

      if (this.option.yAxis instanceof Array) {
        const yAxis = this.option.yAxis.map((yAxis) => {
          const { position, offset, show } =
            yAxisPositions.find((pos) => pos.id === yAxis.id) ?? {};
          yAxis.position = position;
          yAxis.offset = offset;
          return yAxis;
        });
        this.chartInstance?.setOption(
          { yAxis },
          {
            silent: true,
            lazyUpdate: true,
          },
        );
      }
    });
  }

  getOption(): echarts.EChartsOption {
    // filter series with data
    this.relevantSeries = this.series.filter(
      (serie) =>
        serie.meta.skipFilter ||
        serie.value.encode != null ||
        (serie.value.data != null && (serie.value.data as Array<unknown>).length > 0),
    );
    // filter yAxis that are being used
    this.relevantYAxis = this.yAxis.filter(
      (yAxis) => this.relevantSeries.find((serie) => serie.meta.yAxisId === yAxis.meta.id) != null,
    );
    // filter xAxis that are being used
    const relevantXAxis = this.xAxis.filter((xAxis) =>
      this.relevantSeries.find((serie) => serie.meta.xAxisId === xAxis.meta.id),
    );
    const seriesColors = generateSeriesColors(
      this.relevantSeries.map((serie) => ({
        dataType: serie.meta.dataType,
        color: serie.meta.color,
      })),
    );
    const yAxisPositions = generateYAxisPositions(
      this.relevantYAxis.map((yAxis) => ({
        id: yAxis.meta.id,
        position: yAxis.value.position,
        gridIndex: yAxis.meta.gridIndex ?? 0,
        show: yAxis.value.show !== false,
      })),
      this.relevantSeries.map((serie) => serie.meta.yAxisId),
    );

    this.option = {
      ...createBaseOptionEChart(this.initOptions),
      yAxis:
        this.relevantYAxis.length > 0
          ? [
              ...this.relevantYAxis.map((yAxis) => {
                const { position, offset, show } =
                  yAxisPositions.find((pos) => pos.id === yAxis.meta.id) ?? {};
                return {
                  ...yAxis.value,
                  position,
                  offset,
                  ...(!show
                    ? ({
                        show: false,
                        axisLabel: {
                          show: false,
                        },
                        axisPointer: {
                          type: 'none',
                          show: false,
                        },
                      } as const)
                    : {
                        axisLabel: {
                          ...yAxis.value.axisLabel,
                          hideOverlap: true,
                          // rotate: 45,
                          margin: 20,
                        },
                      }),
                };
              }),
            ]
          : [{ type: 'value' }],
      xAxis:
        relevantXAxis?.length > 0 ? relevantXAxis.map((xAxis) => xAxis.value) : { type: 'time' },
      series: this.relevantSeries.map((serie, index) => ({
        ...serie.value,
        color: seriesColors[index],
      })),
    };

    return this.option;
  }

  getYAxisIdFromSerieId(serieId: string | undefined): string | null {
    return this.series.find((serie) => serie.meta.id === serieId)?.meta.yAxisId ?? null;
  }

  getSerieUnit(yAxisId: string): string | null {
    return this.yAxis.find((yAxis) => yAxis.meta.id === yAxisId)?.meta.unit ?? null;
  }

  getSeriePrecision(yAxisId: string): number {
    return this.yAxis.find((yAxis) => yAxis.meta.id === yAxisId)?.meta.precision ?? 1;
  }

    private addBottomXAxis(meta:  InternalXAxis['meta'], xAxis: InternalXAxis['value']) {
    this.xAxis.push({
      meta: {
        ...meta,
        id: `${meta.id}-bottom`,
        gridIndex: 1,
      },
      value: {
        ...xAxis,
        id: `${meta.id}-bottom`,
        gridIndex: 1,
      },
    });
  }



  addXAxis(
    meta: InternalXAxis['meta'],
    value: ((base: InternalXAxis['value']) => InternalXAxis['value']) | InternalXAxis['value'] = (
      options,
    ) => options,
  ) {
    const xAxis =
      typeof value !== 'function'
        ? value
        : value({
            id: meta.id,
            type: meta.type ?? 'time',
            axisLabel: {},
            ...(meta.type === 'time' || !meta.type
              ? ({
                  type: 'time',
                  axisPointer: {
                    label: {
                      formatter: (params) => {
                        const date = new Date(params.value);
                        if (!this.formatters) return date.toLocaleString();
                        return this.formatters.formatDateTime(date);
                      },
                    },
                  },
                  axisLabel: {
                    rich: {
                      month: {
                        fontWeight: 'bold',
                      },
                    },
                    formatter: (value) => formatXAxisLabel(value, this.formatters),
                  },
                } as const)
              : {}),
            ...(meta.gridIndex != null ? { gridIndex: meta.gridIndex } : {}),
          });
    this.xAxis.push({ meta, value: xAxis });
    this.addBottomXAxis(meta, xAxis);
    return this;
  }

  addYAxis(
    meta: InternalYAxis['meta'],
    value: ((base: InternalYAxis['value']) => InternalYAxis['value']) | InternalYAxis['value'] = (
      options,
    ) => options,
  ) {
    const { id, type, unit } = meta;
    const precision = meta.precision ?? 1;

    const yAxis =
      typeof value !== 'function'
        ? value
        : value({
            id,
            ...(meta.gridIndex ? { gridIndex: meta.gridIndex } : {}),
            type: type ?? 'value',
            splitLine: {
              show: true,
              lineStyle: {
                color: 'rgba(150,150,150, 0.1)',
              },
            },
            axisPointer: {
              snap: true,
            },
            alignTicks: true,
            boundaryGap: [0, '20%'],
            tooltip: {
              show: false,
            },
            axisLabel: {
              formatter: (value: number | string) => {
                if (typeof value === 'string') return value;
                if (value === null) return 'N/A';
                const formattedValue = Object.values(MetricUnit).includes(unit as MetricUnit)
                  ? this.formatters?.formatMetricValue(value, {
                      unit: unit as MetricUnit,
                      precision: precision ?? 1,
                    })
                  : this.formatters?.formatNumber(value, precision) + (unit ? ` ${unit}` : '');
                return formattedValue ?? '';
              },
              color: 'rgba(255,255,255, 0.7)',
              showMinLabel: false,
            },
          });
    this.yAxis.push({
      meta,
      value: yAxis,
    });
    this.yAxis.push({
      meta: {
        ...meta,
        id: `${id}-bottom`,
        gridIndex: 1,
        precision,
      },
      value: {
        ...yAxis,
        id: `${id}-bottom`,
        gridIndex: 1,
        boundaryGap: [0, '80%'],
        axisLabel: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
    });

    return this;
  }

  addSeries(
    series: {
      meta: InternalSerie['meta'];
      value?:
        | ((options: InternalSerie['value']) => InternalSerie['value'])
        | InternalSerie['value'];
    }[],
    replace?: boolean,
  ) {
    if (replace) {
      this.series = [];
    }
    for (const serie of series) {
      const serieBase: SerieType = {
        id: serie.meta.id ?? serie.meta.name,
        yAxisId: serie.meta.yAxisId,
        xAxisId: serie.meta.xAxisId,
        data: serie.meta.data,
        name: serie.meta.name,
        ...(serie.meta.color != null ? { color: serie.meta.color } : {}),
        type: serie.meta.type === 'area' ? 'line' : serie.meta.type,
        symbol: 'circle',
        emphasis: {
          focus: 'series',
        },
        tooltip: {
          valueFormatter: (value) => {
            if (value == null) return 'N/A';
            if (isNaN(Number(value)) || !this.formatters) return String(value);
            const unit = this.getSerieUnit(serie.meta.yAxisId) ?? MetricUnit.None;
            const precision = this.getSeriePrecision(serie.meta.yAxisId) ?? 1;
            if (Object.values(MetricUnit).includes(unit as MetricUnit))
              return this.formatters.formatMetricValue(value as number, {
                unit: unit as MetricUnit,
                precision,
              });
            return `${this.formatters?.formatNumber(value as number, precision)} ${unit}`;
          },
        },
        ...(serie.meta.type === 'area' ? { areaStyle: { opacity: 0.2 } } : {}),
      };
      this.series.push({
        meta: serie.meta,
        value: serie.value
          ? typeof serie.value === 'function'
            ? serie.value(serieBase)
            : serie.value
          : serieBase,
      });
    }
    return this;
  }

  updateSerie(
    serieId: string,
    updater: (serie: any) => any
  ) {
    const serieIndex = this.series.findIndex((s) => s.meta.id === serieId);

    if (serieIndex === -1) {
      return this;
    }

    const currentSerie = this.series[serieIndex];
    const updatedValue = updater(currentSerie.value);

    this.series[serieIndex] = {
      ...currentSerie,
      value: updatedValue
    };

    return this;
  }
  
  clone() {
    const cloned = new EChartsOptionBuilder(this.initOptions);
    cloned.formatters = this.formatters;
    // Clonar profundamente los arrays de ejes X
    cloned.xAxis = this.xAxis.map(axis => ({
      meta: { ...axis.meta },
      value: { ...axis.value },
    }));

    // Clonar profundamente los arrays de ejes Y
    cloned.yAxis = this.yAxis.map(axis => ({
      meta: { ...axis.meta },
      value: { ...axis.value },
    }));

    // Clonar profundamente las series
    cloned.series = this.series.map(serie => ({
      meta: { ...serie.meta },
      value: { ...serie.value },
    }));

    // Copiar los arrays relevantes (estos se recalculan en getOption)
    cloned.relevantYAxis = this.relevantYAxis.map(axis => ({
      meta: { ...axis.meta },
      value: { ...axis.value },
    }));

    cloned.relevantSeries = this.relevantSeries.map(serie => ({
      meta: { ...serie.meta },
      value: { ...serie.value },
    }));

    // Copiar la opción actual (shallow copy es suficiente ya que se recalcula)
    cloned.option = { ...this.option };
    return cloned;
  }
}
